import type React from 'react'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import {
  DashboardRestrictedWordLogControllerService,
  RestrictedWordUsage,
} from '@api'
import { useUnit } from 'effector-react'
import type { DefaultValues, FieldValues } from 'react-hook-form'
import type { Resolver } from 'react-hook-form/dist/types/resolvers'
import { type BaseElement, type BaseText, type Descendant } from 'slate'

import { DIALOG_TYPE } from '@/dialogs/constants'
import { $account } from '@/models/account.ts'
import { $creatorId } from '@/models/creators'
import { openDialog } from '@/models/dialogs'
import type {
  DefaultMessageEditorValues,
  MessageEditorComponent,
} from '@/modules/message/components/editor/schema'
import { defaultComponents } from '@/modules/message/components/editor/schema'
import {
  PlaceholderEditor,
  useEditor,
} from '@/modules/message/components/placeholder/placeholder-editor'
import type { MessageBase } from '@/modules/message/components/sequence/message-sequence-editor-table'
import { MessageContextType } from '@/modules/message/components/sequence/types.ts'
import { useCheckRestrictedWords } from '@/modules/restricted-words/hooks/use-check-restricted-words.ts'

import { CUSTOM_NAME_PLACEHOLDER_PATTERN, PLACEHOLDER_PATTERN } from './consts'

type OnSubmitHandler = (value: InferMessageEditorOutput<MessageBase>) => void

export interface MessageEditorContextProps {
  editor: PlaceholderEditor
  components: readonly MessageEditorComponent[]
  defaultValues: DefaultValues<MessageBase>
  isSubmitLoading: boolean
  onSubmit: OnSubmitHandler
  onChange: (value: Descendant[]) => void
  isEmpty: boolean
  schema?: Resolver<MessageBase>
  placeholder?: string
  hasPlaceholders: boolean
  setHasPlaceholders: (value: boolean) => void
  hasCustomNamePlaceholders: boolean
  setHasCustomNamePlaceholders: (value: boolean) => void
  updatePlaceholderState: (text: string) => void
  insert: (text: { id: string; name: string }) => void
}

const MessageEditorContext = createContext<
  MessageEditorContextProps | undefined
>(undefined)

export type InferMessageEditorOutput<T> = T & { text: string }

const contextTypeToRestrictedWordUsage: Record<
  MessageContextType,
  RestrictedWordUsage
> = {
  [MessageContextType.ExpiringFans]:
    RestrictedWordUsage.AUTO_MESSAGE_EXPIRING_FANS,
  [MessageContextType.MassMessage]: RestrictedWordUsage.MASS_MESSAGES,
  [MessageContextType.FollowUpMessage]: RestrictedWordUsage.FOLLOW_UP_MESSAGES,
  [MessageContextType.WelcomeMessage]: RestrictedWordUsage.WELCOME_MESSAGES,
}

export interface MessageEditorProviderProps<T extends FieldValues> {
  children: React.ReactNode
  onSubmit: (state: InferMessageEditorOutput<T>) => void
  defaultValues?: DefaultMessageEditorValues<MessageBase>
  schema?: Resolver<T>
  components?: readonly MessageEditorComponent[]
  textPlaceholder?: string
  type?: MessageContextType
}

export const MessageEditorProvider = <T extends MessageBase>(
  props: MessageEditorProviderProps<T>,
) => {
  const {
    children,
    onSubmit,
    components,
    defaultValues,
    schema,
    textPlaceholder,
    type,
  } = props

  const creatorId = useUnit($creatorId) ?? 0
  const account = useUnit($account)
  const [isEmpty, setIsEmpty] = useState(true)
  const [hasPlaceholders, setHasPlaceholders] = useState(false)
  const [hasCustomNamePlaceholders, setHasCustomNamePlaceholders] =
    useState(false)
  const { checkRestrictedWords, isCheckRestrictedWordsLoading } =
    useCheckRestrictedWords()

  const updatePlaceholderState = useCallback((text: string) => {
    // Check if the text has placeholders
    const matches = text.match(PLACEHOLDER_PATTERN)

    setHasPlaceholders(!!matches?.length)
    setHasCustomNamePlaceholders(
      !!text.match(CUSTOM_NAME_PLACEHOLDER_PATTERN)?.length,
    )
  }, [])

  useEffect(() => {
    updatePlaceholderState(defaultValues?.text ?? '')
  }, [defaultValues?.text, updatePlaceholderState])

  const editor = useEditor()

  const handleChange = useCallback((newValue: Descendant[]) => {
    const isInputEmpty = !(newValue[0] as BaseElement).children.some(
      (child) => !!(child as BaseText).text,
    )

    setIsEmpty(isInputEmpty)
  }, [])

  const handleSubmit: OnSubmitHandler = useCallback(
    async (message) => {
      const onProceed = () => {
        ;(onSubmit as OnSubmitHandler)(message)
      }

      if (message.text) {
        const res = await checkRestrictedWords(message.text)

        if (account && type && res) {
          DashboardRestrictedWordLogControllerService.dashboardAddRestrictedWordLog(
            {
              creatorId: creatorId,
              requestBody: {
                restrictedWordUsage: contextTypeToRestrictedWordUsage[type],
                restrictionType: res.restrictionType,
                restrictedWord: res.restrictedWord,
                restrictedWordId: res.id,
                message: message.text,
                fanId: account.userId!,
              },
            },
          )
        }

        if (res !== null) {
          openDialog({
            type: DIALOG_TYPE.BLACKLISTED_WORD_USAGE_DETECTION,
            match: res,
          })

          return
        } else {
          onProceed()
        }
      } else {
        onProceed()
      }
    },
    [account, checkRestrictedWords, onSubmit, type, creatorId],
  )

  const context = useMemo<MessageEditorContextProps>(
    () => ({
      editor,
      placeholder: textPlaceholder,
      hasPlaceholders,
      setHasPlaceholders,
      hasCustomNamePlaceholders,
      setHasCustomNamePlaceholders,
      updatePlaceholderState,
      onSubmit: handleSubmit,
      onChange: handleChange,
      isEmpty,
      isSubmitLoading: isCheckRestrictedWordsLoading,
      defaultValues: defaultValues as DefaultValues<MessageBase>,
      schema: schema as Resolver<MessageBase>,
      components: components ?? defaultComponents,
      insert: (text: { id: string; name: string }) => {
        PlaceholderEditor.insert(editor, text)
        setHasPlaceholders(true)

        if (text.id === 'name' || text.id === 'lname' || text.id === 'uname') {
          setHasCustomNamePlaceholders(true)
        }
      },
    }),
    [
      textPlaceholder,
      components,
      defaultValues,
      isEmpty,
      editor,
      hasPlaceholders,
      hasCustomNamePlaceholders,
      updatePlaceholderState,
      handleSubmit,
      handleChange,
      schema,
      isCheckRestrictedWordsLoading,
    ],
  )

  return (
    <MessageEditorContext.Provider value={context}>
      {children}
    </MessageEditorContext.Provider>
  )
}

export function useMessageEditor() {
  const context = useContext(MessageEditorContext)

  if (!context) {
    throw new Error(
      'useMessageEditor must be used within a MessageEditorProvider',
    )
  }

  return context
}
